import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

import "./Dialog.css";

/**
 * Minimal modal: focus moves in on open, Tab is trapped inside it, Escape
 * and a backdrop click close it, focus returns to whatever opened it.
 *
 * Deliberately not the native `<dialog>` element — jsdom's support for
 * `showModal()`/focus-trapping is incomplete (it logs "not implemented" for
 * parts of it), which would make this untestable under Vitest; a plain
 * controlled overlay behaves identically in real browsers and in tests.
 *
 * `size="wide"` widens it for a full data-entry form (the intake/edit modal);
 * the default stays a compact confirmation width. `dismissable={false}` turns
 * off Escape and backdrop-click closing, keeping only the explicit close
 * controls — for the entry form, where a stray click on the backdrop would
 * otherwise throw away everything typed. The × button always closes regardless.
 */
export function Dialog({
  title,
  onClose,
  children,
  size = "default",
  dismissable = true,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  size?: "default" | "wide";
  dismissable?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const container = containerRef.current;
    const focusable = container?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.[0]?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && dismissable) {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key === "Tab" && focusable && focusable.length > 0) {
        const first = focusable[0]!;
        const last = focusable[focusable.length - 1]!;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      previouslyFocused.current?.focus();
    };
  }, [onClose, dismissable]);

  return createPortal(
    <div
      className="dialog-overlay"
      onMouseDown={(event) => {
        if (dismissable && event.target === event.currentTarget) onClose();
      }}
    >
      <div className={`dialog${size === "wide" ? " dialog--wide" : ""}`} role="dialog" aria-modal="true" aria-label={title} ref={containerRef}>
        <div className="dialog-header">
          <h2>{title}</h2>
          <button type="button" className="dialog-close" onClick={onClose} aria-label="Zapri">
            ×
          </button>
        </div>
        <div className="dialog-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
